import { CartErrorEvent } from '@theme/events';
import { notifyCartAddAndOpen } from './vestfirma-cart-after-add.js';

const MAX_QTY = 999;

/** Ordem padrão Vestfirma — grade atacado (P → M → G → GG). */
const SIZE_SORT_ORDER = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XXG', 'XXXG', '2XG', '3XG', '4XG'];
const DEFAULT_COLOR_ALIASES = ['preto', 'preta', 'black'];

function normalizeColorLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function sizeSortRank(label) {
  const normalized = String(label || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const known = SIZE_SORT_ORDER.indexOf(normalized);
  if (known >= 0) return known;

  const numeric = Number.parseInt(normalized, 10);
  if (!Number.isNaN(numeric)) return 100 + numeric;

  return 1000 + normalized.charCodeAt(0);
}

function sortSizeLabels(labels) {
  return [...labels].sort((a, b) => {
    const diff = sizeSortRank(a) - sizeSortRank(b);
    return diff !== 0 ? diff : String(a).localeCompare(String(b), 'pt-BR');
  });
}

function formatMoney(cents, format) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents, format);
  }
  const value = (cents / 100).toFixed(2).replace('.', ',');
  return `R$ ${value}`;
}

function applyI18nTemplate(template, vars) {
  let output = template;
  Object.entries(vars).forEach(([key, value]) => {
    output = output.replaceAll(`{{ ${key} }}`, String(value));
    output = output.replaceAll(`__${key.toUpperCase()}__`, String(value));
  });
  return output;
}

function isPlainPieceHandle(handle) {
  const slug = String(handle || '').toLowerCase();
  return (
    slug.includes('-lisa') ||
    slug.includes('peca-lisa') ||
    slug.includes('sem-estampa') ||
    /(?:^|-)lisa(?:-|$)/.test(slug)
  );
}

class VestfirmaPdpWholesale extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.config = this.readConfig();
    if (!this.config) return;

    this.hidePlainPieceCustomization();

    this.selection = {};
    this.quantities = new Map();
    this.selectedQtyOnlySize = '';
    this.file = null;
    this.stampLocation = '';
    this._busy = false;

    this.cacheDom();
    this.initSelectionFromDom();
    this.initDefaultColorSelection();
    this.bindEvents();
    this.initStandardQuantitySelector();
    this.renderTierTable();
    this.renderSizeGrid();
    this.initStickyBar();
    this.updateTotal();
  }

  get pdpRoot() {
    return this.closest('.md-pdp') || document;
  }

  getColorGroups() {
    return this.pdpRoot.querySelectorAll('[data-vf-color-group]');
  }

  setColorSelection(position, value) {
    this.getColorGroups().forEach((group) => {
      group.querySelectorAll('[data-vf-option-value]').forEach((el) => {
        const selected = el.dataset.vfOptionValue === value;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      const current = group.querySelector('[data-vf-option-current]');
      if (current) {
        current.textContent = value;
        current.hidden = false;
      }
    });

    this.selection[position] = value;
    this.quantities.clear();
    this.selectedQtyOnlySize = '';
    this.syncGalleryForColor(value);
    this.sizesWrap?.classList.remove('is-color-error');
    this.sizeHint?.classList.remove('is-error');
    this.renderSizeGrid();
    this.renderStickyBar();
    this.updateTotal();
  }

  readConfig() {
    const node = this.querySelector('[data-vf-wholesale-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  hidePlainPieceCustomization() {
    const handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const handle = handleMatch ? handleMatch[1] : '';
    const customizable = this.getAttribute('data-vf-customizable');
    const isPlain =
      customizable === '0' || isPlainPieceHandle(handle);

    if (!isPlain) return;

    this.querySelector('.vf-wholesale__upload')?.remove();
    this.querySelector('[data-vf-stamp-location]')?.remove();
    if (this.config) {
      this.config.showStampLocation = false;
    }
  }

  cacheDom() {
    this.tierTable = this.querySelector('[data-vf-tier-table]');
    this.tier1Row = this.querySelector('[data-vf-tier-row="1"]');
    this.tier2Row = this.querySelector('[data-vf-tier-row="2"]');
    this.tier1Price = this.querySelector('[data-vf-tier-1-price]');
    this.tier2Price = this.querySelector('[data-vf-tier-2-price]');
    this.tierHint = this.querySelector('[data-vf-tier-hint]');
    this.heroUnitPrice = this.querySelector('[data-vf-hero-unit-price]');
    this.heroInstallments = this.querySelector('[data-vf-hero-installments]');
    this.sizeGrid = this.querySelector('[data-vf-size-grid]');
    this.sizeHint = this.querySelector('[data-vf-size-hint]');
    this.sizesWrap =
      this.querySelector('[data-vf-sizes-wrap]') || this.querySelector('[data-vf-qty-only-wrap]');
    this.standardQtyHost = this.querySelector('[data-vf-standard-qty-host]');
    this.qtyOnlySizeList = this.querySelector('[data-vf-qty-only-size-list]');
    this.standardQtyInput = this.standardQtyHost?.querySelector('input[type="number"]') || null;
    this.standardQtyMinus = this.standardQtyHost?.querySelector('.quantity-minus') || null;
    this.standardQtyPlus = this.standardQtyHost?.querySelector('.quantity-plus') || null;
    this.totalEl = this.querySelector('[data-vf-total]');
    this.submitBtn = this.querySelector('[data-vf-submit]');
    this.dropzone = this.querySelector('[data-vf-upload-dropzone]');
    this.fileInput = this.querySelector('[data-vf-upload-input]');
    this.uploadPreview = this.querySelector('[data-vf-upload-preview]');
    this.uploadThumb = this.querySelector('[data-vf-upload-thumb]');
    this.uploadName = this.querySelector('[data-vf-upload-name]');
    this.uploadRemove = this.querySelector('[data-vf-upload-remove]');
    this.stampLocationWrap = this.querySelector('[data-vf-stamp-location]');
    this.stickyRoot = this.querySelector('[data-vf-wholesale-sticky]');
    this.stickyBar = this.stickyRoot?.querySelector('[data-vf-sticky-bar]') || null;
    this.stickyCta = this.stickyRoot?.querySelector('[data-vf-sticky-cta]') || null;
    this.stickySentinel = this.querySelector('[data-vf-wholesale-sticky-sentinel]');
    this._stickyHiddenByFooter = false;
  }

  initSelectionFromDom() {
    this.querySelectorAll('[data-vf-option-group]').forEach((group) => {
      const position = Number(group.dataset.optionPosition);
      if (!position) return;
      if (this.config.requireColorSelection && position === this.config.colorPosition) return;

      const selected = group.querySelector('.is-selected, [aria-selected="true"]');
      if (selected) {
        this.selection[position] = selected.dataset.vfOptionValue || '';
      }
    });
  }

  resolveDefaultColorValue() {
    const { colorPosition, requireColorSelection, colorOptions, defaultColor } = this.config;
    if (!requireColorSelection || !colorPosition) return null;

    const options = Array.isArray(colorOptions) ? colorOptions.filter(Boolean) : [];
    const normalizedOptions = options.map((option) => ({
      raw: option,
      norm: normalizeColorLabel(option),
    }));

    const preferred = normalizeColorLabel(defaultColor);
    if (preferred) {
      const preferredMatch = normalizedOptions.find((option) => option.norm === preferred);
      if (preferredMatch) return preferredMatch.raw;
    }

    for (const alias of DEFAULT_COLOR_ALIASES) {
      const aliasMatch = normalizedOptions.find((option) => option.norm === alias);
      if (aliasMatch) return aliasMatch.raw;
    }

    const containsPreto = normalizedOptions.find((option) => option.norm.includes('preto'));
    if (containsPreto) return containsPreto.raw;

    for (const group of this.getColorGroups()) {
      const swatches = [...group.querySelectorAll('[data-vf-option-value]')].filter(
        (el) => el.dataset.vfColorAvailable !== 'false' && !el.disabled
      );

      if (preferred) {
        const preferredSwatch = swatches.find(
          (el) => normalizeColorLabel(el.dataset.vfOptionValue) === preferred
        );
        if (preferredSwatch) return preferredSwatch.dataset.vfOptionValue;
      }

      for (const alias of DEFAULT_COLOR_ALIASES) {
        const aliasSwatch = swatches.find(
          (el) => normalizeColorLabel(el.dataset.vfOptionValue) === alias
        );
        if (aliasSwatch) return aliasSwatch.dataset.vfOptionValue;
      }

      if (swatches[0]) return swatches[0].dataset.vfOptionValue;
    }

    return options[0] || null;
  }

  initDefaultColorSelection() {
    const { colorPosition, requireColorSelection } = this.config;
    if (!requireColorSelection || !colorPosition || this.hasColorSelected()) return;

    const defaultColor = this.resolveDefaultColorValue();
    if (!defaultColor) return;

    this.setColorSelection(colorPosition, defaultColor);
  }

  hasColorSelected() {
    const { colorPosition, requireColorSelection } = this.config;
    if (!requireColorSelection || !colorPosition) return true;
    return Boolean(this.selection[colorPosition]);
  }

  hasSizeSelected() {
    if (!this.isQuantityOnlyMode()) return true;
    const labels = this.getAllSizeLabels();
    if (!labels.length) return true;
    return Boolean(this.selectedQtyOnlySize);
  }

  syncSizeHint() {
    if (!this.sizeHint) return;

    if (this.isQuantityOnlyMode()) {
      const awaitingColor = this.config.requireColorSelection && !this.hasColorSelected();
      const awaitingSize = this.hasColorSelected() && !this.hasSizeSelected();
      this.sizeHint.hidden = false;
      this.sizesWrap?.classList.toggle('is-awaiting-color', awaitingColor);
      this.sizesWrap?.classList.toggle('is-awaiting-size', awaitingSize);
      if (!awaitingColor && !awaitingSize) {
        this.sizesWrap?.classList.remove('is-color-error', 'is-size-error');
        this.sizeHint.classList.remove('is-error');
        if (this.config.i18n?.quantityOnlyHint) {
          this.sizeHint.textContent = this.config.i18n.quantityOnlyHint;
        }
      }
      return;
    }

    const awaitingColor = this.config.requireColorSelection && !this.hasColorSelected();
    this.sizeHint.hidden = !awaitingColor;
    this.sizesWrap?.classList.toggle('is-awaiting-color', awaitingColor);
    if (!awaitingColor) {
      this.sizesWrap?.classList.remove('is-color-error');
      this.sizeHint.classList.remove('is-error');
    }
  }

  showSizeColorRequiredError() {
    if (!this.config.requireColorSelection || this.hasColorSelected()) {
      this.showSizeRequiredError();
      return;
    }

    this.sizesWrap?.classList.add('is-color-error');
    this.sizeHint?.classList.add('is-error');
    if (this.sizeHint && this.config.i18n?.selectColorFirst) {
      this.sizeHint.textContent = this.config.i18n.selectColorFirst;
    }
    this.sizeHint.hidden = false;
    this.sizesWrap?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  showSizeRequiredError() {
    if (!this.isQuantityOnlyMode() || this.hasSizeSelected()) return;

    this.sizesWrap?.classList.add('is-size-error');
    this.sizeHint?.classList.add('is-error');
    if (this.sizeHint && this.config.i18n?.selectSizeFirst) {
      this.sizeHint.textContent = this.config.i18n.selectSizeFirst;
    }
    this.sizeHint.hidden = false;
    this.qtyOnlySizeList?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  promptMissingSelection() {
    if (!this.hasColorSelected()) {
      this.showSizeColorRequiredError();
      return;
    }

    if (!this.hasSizeSelected()) {
      this.showSizeRequiredError();
      return;
    }

    if (this.getTotalQuantity() < 1) {
      this.sizesWrap?.classList.add('is-size-error');
      if (this.sizeHint) {
        this.sizeHint.classList.add('is-error');
        this.sizeHint.hidden = false;
        this.sizeHint.textContent = this.config.i18n.selectOne || this.config.i18n.selectSizeFirst || '';
      }
      (this.qtyOnlySizeList || this.sizesWrap)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  bindSizeGridGuard() {
    if (!this.sizeGrid || this._sizeGridGuardBound) return;
    this._sizeGridGuardBound = true;

    const blockIfNoColor = (event) => {
      if (this.hasColorSelected()) return;
      const interactive = event.target.closest(
        '[data-vf-qty-minus], [data-vf-qty-plus], [data-vf-qty-input], .vf-wholesale__size-cell'
      );
      if (!interactive || !this.sizeGrid.contains(interactive)) return;

      event.preventDefault();
      event.stopPropagation();
      this.showSizeColorRequiredError();
    };

    this.sizeGrid.addEventListener('pointerdown', blockIfNoColor, true);
    this.sizeGrid.addEventListener('keydown', (event) => {
      if (this.hasColorSelected()) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      blockIfNoColor(event);
    });
    this.sizeGrid.addEventListener('focusin', (event) => {
      if (this.hasColorSelected()) return;
      if (!event.target.matches('[data-vf-qty-input]')) return;
      event.target.blur();
      this.showSizeColorRequiredError();
    });
  }

  getFirstVariantForColor(colorValue) {
    const { colorPosition, variants, colorSource } = this.config;
    if (!colorPosition || !colorValue) return null;

    if (colorSource === 'images' || colorSource === 'category') {
      return this.getDefaultVariant();
    }

    const match = (predicate) =>
      variants.find((variant) => {
        if (this.getOptionValue(variant, colorPosition) !== colorValue) return false;
        return predicate(variant);
      });

    return match((v) => v.available !== false) || match(() => true) || null;
  }

  syncGalleryForColor(colorValue) {
    const pdp = this.closest('[data-md-pdp]');
    if (!pdp || !colorValue) return;

    const variant = this.getFirstVariantForColor(colorValue);
    if (
      window.VestfirmaPdpStickyGallery &&
      typeof window.VestfirmaPdpStickyGallery.updateForColorGroup === 'function'
    ) {
      window.VestfirmaPdpStickyGallery.updateForColorGroup(pdp, colorValue, variant);
      return;
    }

    const page = pdp._vestfirmaPdp;
    if (!page || !page.galleryMap) return;

    const map = page.galleryMap;
    let fullIds = map.byVisual && map.byVisual[colorValue] ? map.byVisual[colorValue].imageIds : null;

    if (fullIds && fullIds.length) {
      fullIds = page.orderGalleryImageIds
        ? page.orderGalleryImageIds(fullIds.map(String), colorValue, variant)
        : fullIds.map(String);
      const lightboxIds = page.buildGalleryFullIds
        ? page.buildGalleryFullIds(fullIds)
        : fullIds;
      const gridIds = page.buildGalleryGridIds
        ? page.buildGalleryGridIds(fullIds)
        : fullIds.slice(0, page.getMaxGallerySlots());
      if (!gridIds) return;
      const primaryId = gridIds[0];
      const hasGalleryVideo = Boolean(map.hasGalleryVideo);

      if (
        !hasGalleryVideo &&
        page.galleryHasFixedLayout &&
        page.galleryHasFixedLayout() &&
        primaryId &&
        primaryId !== 'video' &&
        page.updateGalleryColorImages &&
        page.updateGalleryColorImages(gridIds)
      ) {
        if (window.VestfirmaPdpGallerySync) {
          window.VestfirmaPdpGallerySync.noteVisualChange(colorValue, fullIds);
        }
        page.syncGalleryVideoAutoplay();
        return;
      }

      page.renderGalleryWithImageIds(gridIds, lightboxIds);
      if (window.VestfirmaPdpGallerySync) {
        window.VestfirmaPdpGallerySync.noteVisualChange(colorValue, fullIds);
      }
      page.syncGalleryVideoAutoplay();
      return;
    }

    if (variant && typeof page.updateGalleryFromVariant === 'function') {
      page.updateGalleryFromVariant(variant);
    }
  }

  clearColorSelection() {
    const { colorPosition } = this.config;
    if (!colorPosition) return;

    delete this.selection[colorPosition];
    this.getColorGroups().forEach((group) => {
      group.querySelectorAll('[data-vf-option-value]').forEach((el) => {
        el.classList.remove('is-selected');
        el.setAttribute('aria-selected', 'false');
      });

      const current = group.querySelector('[data-vf-color-current]');
      if (current) {
        current.textContent = '';
        current.hidden = true;
      }
    });
  }

  bindEvents() {
    this.getColorGroups().forEach((group) => {
      group.querySelectorAll('[data-vf-option-value]').forEach((btn) => {
        btn.addEventListener('pointerenter', () => {
          const value = btn.dataset.vfOptionValue;
          if (!value || btn.disabled || btn.dataset.vfColorAvailable === 'false') return;
          const pdp = this.closest('[data-md-pdp]');
          if (
            pdp &&
            window.VestfirmaPdpStickyGallery &&
            typeof window.VestfirmaPdpStickyGallery.prefetchVisualGroup === 'function'
          ) {
            window.VestfirmaPdpStickyGallery.prefetchVisualGroup(pdp, value);
          }
        });
        btn.addEventListener('click', () => {
          const position = Number(group.dataset.optionPosition);
          const value = btn.dataset.vfOptionValue;
          if (!position || !value) return;
          if (btn.disabled || btn.dataset.vfColorAvailable === 'false') return;
          this.setColorSelection(position, value);
        });
      });
    });

    this.querySelectorAll('[data-vf-option-group]:not([data-vf-color-group])').forEach((group) => {
      if (group.dataset.vfOptionSingle === 'true') return;

      group.querySelectorAll('[data-vf-option-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const position = Number(group.dataset.optionPosition);
          const value = btn.dataset.vfOptionValue;
          if (!position || !value) return;
          if (btn.disabled || btn.dataset.vfColorAvailable === 'false') return;

          group.querySelectorAll('[data-vf-option-value]').forEach((el) => {
            el.classList.remove('is-selected');
            el.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-selected', 'true');

          const current = group.querySelector('[data-vf-option-current]');
          if (current) {
            current.textContent = value;
            current.hidden = false;
          }

          this.selection[position] = value;
          this.quantities.clear();
          this.renderSizeGrid();
          this.updateTotal();
        });
      });
    });

    this.submitBtn?.addEventListener('click', () => this.handleSubmit());

    if (this.dropzone && this.fileInput) {
      this.dropzone.addEventListener('click', () => this.fileInput.click());
      this.dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.fileInput.click();
        }
      });
      this.fileInput.addEventListener('change', () => this.setFile(this.fileInput.files?.[0] || null));

      ['dragenter', 'dragover'].forEach((ev) => {
        this.dropzone.addEventListener(ev, (e) => {
          e.preventDefault();
          this.dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach((ev) => {
        this.dropzone.addEventListener(ev, (e) => {
          e.preventDefault();
          this.dropzone.classList.remove('is-dragover');
        });
      });
      this.dropzone.addEventListener('drop', (e) => {
        const file = e.dataTransfer?.files?.[0];
        if (file) this.setFile(file);
      });
    }

    this.uploadRemove?.addEventListener('click', () => this.setFile(null));

    this.stampLocationWrap?.querySelectorAll('[data-vf-stamp-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.vfStampValue;
        if (!value) return;

        this.stampLocationWrap.querySelectorAll('[data-vf-stamp-value]').forEach((el) => {
          el.classList.remove('is-selected');
          el.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
        this.stampLocation = value;
        this.updateTotal();
      });
    });

    this.bindSizeGridGuard();
  }

  initStickyBar() {
    if (!this.stickyRoot || !this.stickyBar || !this.submitBtn || !this.stickyCta) return;

    if (this.stickyRoot.parentElement !== document.body) {
      document.body.appendChild(this.stickyRoot);
    }

    this.setStickyVisible(false);
    this.bindStickyEvents();
    this.renderStickyBar();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setupStickyVisibility();
      });
    });
  }

  getStickyAnchor() {
    const root = this.pdpRoot || this;
    return (
      root.querySelector('[data-vf-sticky-visibility-anchor]') ||
      root.querySelector('[data-md-whatsapp]')?.closest('.vf-wholesale__whatsapp, .md-pdp__whatsapp-wrap') ||
      root.querySelector('[data-md-whatsapp]') ||
      this.stickySentinel ||
      this.submitBtn
    );
  }

  getStickyScrollTargets() {
    const targets = [];
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (isMobile) {
      const mobileColor =
        this.pdpRoot.querySelector('[data-vf-color-mobile-anchor]') ||
        this.pdpRoot.querySelector('[data-vf-color-mobile-anchor] [data-vf-color-group]');
      if (mobileColor) targets.push(mobileColor);
    } else {
      const colorGroup =
        this.querySelector('[data-vf-color-group-host]') ||
        this.querySelector('[data-vf-color-group]') ||
        this.querySelector('[data-vf-option-groups]');
      if (colorGroup) targets.push(colorGroup);
    }

    if (this.sizesWrap) targets.push(this.sizesWrap);

    if (!targets.length) {
      const fallbackColor = this.querySelector('[data-vf-color-group]');
      if (fallbackColor) targets.push(fallbackColor);
      else if (this.submitBtn) targets.push(this.submitBtn);
    }

    return targets;
  }

  getStickyHeaderOffset() {
    const header =
      document.querySelector('#header-component[data-sticky-state="active"]') ||
      document.querySelector('#header-component') ||
      document.querySelector('.header-section');
    const headerRect = header ? header.getBoundingClientRect() : null;
    let offset = headerRect && headerRect.height > 0 ? headerRect.height + 12 : 12;

    const promoBar =
      document.querySelector('.section-countdown') ||
      document.querySelector('.section-promo-trust') ||
      document.querySelector('.section-header-categories');
    if (promoBar) {
      const promoRect = promoBar.getBoundingClientRect();
      if (promoRect.bottom > 0 && promoRect.top < offset) {
        offset = Math.max(offset, promoRect.bottom + 8);
      }
    }

    return offset;
  }

  getStickyLabel() {
    const { i18n, requireColorSelection, colorPosition } = this.config;
    const hasColorOption = requireColorSelection && colorPosition;
    const hasSizes = this.getAllSizeLabels().length > 0;

    if (this.isQuantityOnlyMode()) {
      if (hasColorOption && !this.hasColorSelected()) {
        return i18n.stickySelectColor || i18n.selectColor;
      }
      if (!this.hasSizeSelected()) {
        return i18n.selectSize || i18n.selectOne;
      }
      return i18n.stickySelectColorQty || i18n.quantityOnlyLabel || i18n.selectOne;
    }

    if (hasColorOption && !this.hasColorSelected()) {
      return i18n.stickySelectColor || i18n.selectColor;
    }

    if (hasColorOption && hasSizes) {
      return i18n.stickySelectColorSize || i18n.selectOne;
    }

    if (hasColorOption) {
      return i18n.stickySelectColor || i18n.selectColor;
    }

    return i18n.selectOne;
  }

  getStickyScrollTopOffset() {
    const headerOffset = this.getStickyHeaderOffset();
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const buyColumn = this.pdpRoot?.querySelector('.md-pdp__buy');

    if (isDesktop && buyColumn) {
      const headerGroupHeight = parseFloat(
        getComputedStyle(document.body).getPropertyValue('--header-group-height')
      );
      if (Number.isFinite(headerGroupHeight) && headerGroupHeight > 0) {
        return headerGroupHeight + 16;
      }
    }

    return headerOffset;
  }

  scrollToStickyTarget() {
    const targets = this.getStickyScrollTargets();
    if (!targets.length) return;

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const scrollTopOffset = this.getStickyScrollTopOffset();
    const stickyBarSpace = 88;
    const scrollComfort = isDesktop ? 32 : 72;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleHeight = Math.max(280, viewportHeight - scrollTopOffset - stickyBarSpace);

    const first = targets[0];
    const last = targets[targets.length - 1];
    const firstTop = first.getBoundingClientRect().top + window.scrollY;
    const lastBottom = last.getBoundingClientRect().bottom + window.scrollY;
    const blockHeight = lastBottom - firstTop;

    let top = firstTop - scrollTopOffset - scrollComfort;

    if (blockHeight <= visibleHeight) {
      top =
        firstTop -
        scrollTopOffset -
        scrollComfort -
        Math.max(0, (visibleHeight - blockHeight) / 3);
    }

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

    if (!this.hasColorSelected() && this.config.requireColorSelection) {
      window.setTimeout(() => this.showSizeColorRequiredError(), 320);
    }
  }

  setupStickyVisibility() {
    const anchor = this.getStickyAnchor();
    if (!anchor || !this.stickyBar || this._stickyVisibilityReady) return;
    this._stickyVisibilityReady = true;

    const evaluate = (entry) => {
      if (!anchor) return;

      if (this._stickyHiddenByFooter) {
        this.setStickyVisible(false);
        return;
      }

      const rect = entry?.boundingClientRect || anchor.getBoundingClientRect();
      const intersecting =
        entry?.isIntersecting ??
        (rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom > 0);

      if (intersecting) {
        this.setStickyVisible(false);
        return;
      }

      if (rect.top < 0) {
        this.setStickyVisible(true);
        return;
      }

      this.setStickyVisible(false);
    };

    this._stickyEvaluate = () => evaluate();

    this._stickyObserver = new IntersectionObserver(
      (entries) => {
        evaluate(entries[0]);
      },
      { threshold: [0, 1], rootMargin: '0px 0px 0px 0px' }
    );
    this._stickyObserver.observe(anchor);

    const footer = document.querySelector('footer.shopify-section, footer');
    if (footer) {
      this._stickyFooterObserver = new IntersectionObserver(
        (entries) => {
          this._stickyHiddenByFooter = Boolean(entries[0]?.isIntersecting);
          evaluate();
        },
        { threshold: 0, rootMargin: '0px 0px 120px 0px' }
      );
      this._stickyFooterObserver.observe(footer);
    }

    const onScrollOrResize = () => {
      if (this._stickyScrollRaf) return;
      this._stickyScrollRaf = window.requestAnimationFrame(() => {
        this._stickyScrollRaf = 0;
        evaluate();
      });
    };

    this._stickyOnScroll = onScrollOrResize;
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('pageshow', onScrollOrResize);

    this.setStickyVisible(false);
    onScrollOrResize();
  }

  setStickyVisible(show) {
    if (!this.stickyRoot || !this.stickyBar) return;

    this.stickyRoot.hidden = !show;
    this.stickyRoot.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.stickyBar.dataset.stuck = show ? 'true' : 'false';
    document.documentElement.classList.toggle('vf-wholesale-sticky-open', show);
  }

  bindStickyEvents() {
    this.stickyCta?.addEventListener('click', () => this.scrollToStickyTarget());
  }

  renderStickyBar() {
    if (!this.stickyCta) return;
    this.stickyCta.textContent = this.getStickyLabel();
  }

  setQuantityForSizeLabel(label, next, { sourceCell = null } = {}) {
    if (!label) return;

    if (!this.hasColorSelected()) {
      this.showSizeColorRequiredError();
      return;
    }

    const variant = this.getVariantForSize(label);
    if (!variant) return;

    const qtyKey = this.quantityKey(variant.id, label);
    const value = Math.max(0, Math.min(MAX_QTY, next));

    if (value <= 0) this.quantities.delete(qtyKey);
    else this.quantities.set(qtyKey, value);

    if (sourceCell) {
      const input = sourceCell.querySelector('[data-vf-qty-input]');
      const minus = sourceCell.querySelector('[data-vf-qty-minus]');
      if (input) input.value = String(value).padStart(2, '0');
      if (minus) minus.disabled = value <= 0;
      sourceCell.classList.toggle('has-qty', value > 0);
    } else {
      this.renderSizeGrid();
    }

    this.updateTotal();
  }

  renderTierTable() {
    if (!this.config.showTierTable || !this.tierTable) return;

    const tier1Price = this.config.tier1UnitPrice;
    const tier2Price =
      this.config.tier2UnitPrice > 0 ? this.config.tier2UnitPrice : tier1Price;

    if (this.tier1Price) {
      this.tier1Price.textContent = applyI18nTemplate(this.config.i18n.tierUnitTemplate, {
        price: formatMoney(tier1Price, this.config.moneyFormat),
      });
    }
    if (this.tier2Price) {
      this.tier2Price.textContent = applyI18nTemplate(this.config.i18n.tierUnitTemplate, {
        price: formatMoney(tier2Price, this.config.moneyFormat),
      });
    }

    this.tierTable.hidden = false;
    this.updateTierHighlight(this.getTotalQuantity());
  }

  getOptionValue(variant, position) {
    if (position === 1) return variant.option1;
    if (position === 2) return variant.option2;
    if (position === 3) return variant.option3;
    return '';
  }

  isQuantityOnlyMode() {
    return Boolean(this.config.quantityOnly);
  }

  isCategorySizeMode() {
    if (this.isQuantityOnlyMode()) return false;
    const { sizePosition, sizeOptions, hasOnlyDefaultVariant } = this.config;
    return (
      Array.isArray(sizeOptions) &&
      sizeOptions.length > 0 &&
      (!sizePosition || hasOnlyDefaultVariant)
    );
  }

  isInferredColorMode() {
    const source = this.config.colorSource;
    return source === 'images' || source === 'category';
  }

  getDefaultVariant() {
    return (
      this.config.variants.find((variant) => variant.available !== false) ||
      this.config.variants[0] ||
      null
    );
  }

  quantityKey(variantId, sizeLabel) {
    if (this.isCategorySizeMode() && sizeLabel) {
      return `size:${sizeLabel}`;
    }
    return String(variantId);
  }

  getMatchingVariants() {
    const { variants, sizePosition, hasOnlyDefaultVariant } = this.config;
    if (hasOnlyDefaultVariant) {
      return variants.filter((variant) => variant.available !== false);
    }
    if (!sizePosition) return [];

    return variants.filter((variant) => {
      return Object.entries(this.selection).every(([pos, value]) => {
        if (Number(pos) === sizePosition) return true;
        return this.getOptionValue(variant, Number(pos)) === value;
      });
    });
  }

  getAllSizeLabels() {
    const { sizeOptions, sizePosition, variants } = this.config;
    if (Array.isArray(sizeOptions) && sizeOptions.length) {
      const valid = sizeOptions.every((label) => typeof label === 'string' && label.length <= 8);
      if (valid) return sortSizeLabels(sizeOptions);
    }

    if (!sizePosition) return [];
    const seen = new Set();
    const labels = [];
    variants.forEach((variant) => {
      const size = this.getOptionValue(variant, sizePosition);
      if (!size || seen.has(size)) return;
      seen.add(size);
      labels.push(size);
    });
    return sortSizeLabels(labels);
  }

  getVariantForSize(sizeLabel) {
    if (this.isCategorySizeMode()) {
      if (this.config.requireColorSelection && !this.hasColorSelected()) return null;
      return this.getDefaultVariant();
    }

    const { sizePosition, colorPosition } = this.config;
    if (!sizePosition || !sizeLabel) return null;
    if (colorPosition && !this.selection[colorPosition]) return null;

    return (
      this.config.variants.find((variant) => {
        if (this.getOptionValue(variant, sizePosition) !== sizeLabel) return false;
        return Object.entries(this.selection).every(([pos, value]) => {
          if (Number(pos) === sizePosition) return true;
          return this.getOptionValue(variant, Number(pos)) === value;
        });
      }) || null
    );
  }

  getSizeValues() {
    const { hasOnlyDefaultVariant, i18n } = this.config;

    if (this.isCategorySizeMode()) {
      const variant = this.getDefaultVariant();
      if (!variant) return [];
      return this.getAllSizeLabels().map((label) => ({ label, variant }));
    }

    if (hasOnlyDefaultVariant) {
      const seen = new Set();
      const sizes = [];
      this.getMatchingVariants().forEach((variant) => {
        if (seen.has(variant.id)) return;
        seen.add(variant.id);
        sizes.push({ label: i18n.quantityLabel || 'Qtd', variant });
      });
      return sizes;
    }

    return this.getAllSizeLabels()
      .map((label) => {
        const variant = this.getVariantForSize(label);
        return variant ? { label, variant } : null;
      })
      .filter(Boolean);
  }

  initStandardQuantitySelector() {
    if (!this.isQuantityOnlyMode() || !this.standardQtyHost || this._standardQtyBound) return;
    this._standardQtyBound = true;

    this.addEventListener('quantity-selector:update', (event) => {
      if (!this.isQuantityOnlyMode()) return;
      if (!this.standardQtyHost?.contains(event.target)) return;

      if (!this.hasColorSelected()) {
        this.showSizeColorRequiredError();
        if (this.standardQtyInput) this.standardQtyInput.value = '1';
        this.quantities.clear();
        this.updateTotal();
        return;
      }

      if (!this.hasSizeSelected()) {
        this.showSizeRequiredError();
        if (this.standardQtyInput) this.standardQtyInput.value = '1';
        this.quantities.clear();
        this.updateTotal();
        return;
      }

      const qty = Math.max(1, Math.min(MAX_QTY, Number(event.detail?.quantity) || 1));
      this.setStandardQuantity(qty);
    });
  }

  getSelectedQtyOnlyVariant() {
    if (!this.isQuantityOnlyMode() || !this.hasSizeSelected()) return null;
    return this.getVariantForSize(this.selectedQtyOnlySize);
  }

  getStandardQuantity() {
    const variant = this.getSelectedQtyOnlyVariant();
    if (!variant) return 1;
    return this.quantities.get(String(variant.id)) || 1;
  }

  setStandardQuantity(value) {
    const variant = this.getSelectedQtyOnlyVariant();
    if (!variant) return;

    const qty = Math.max(1, Math.min(MAX_QTY, value));
    this.quantities.clear();
    this.quantities.set(String(variant.id), qty);

    if (this.standardQtyInput) {
      this.standardQtyInput.value = String(qty);
    }
    if (this.standardQtyMinus) {
      this.standardQtyMinus.disabled = !this.canEditQtyOnlyQuantity() || qty <= 1;
    }
    if (this.standardQtyPlus) {
      this.standardQtyPlus.disabled = !this.canEditQtyOnlyQuantity();
    }

    this.updateTotal();
  }

  canEditQtyOnlyQuantity() {
    return this.hasColorSelected() && this.hasSizeSelected();
  }

  ensureStandardQuantityDefault() {
    if (!this.isQuantityOnlyMode() || !this.canEditQtyOnlyQuantity()) return;
    this.setStandardQuantity(this.getStandardQuantity() || 1);
  }

  setQtyOnlySizeSelection(sizeLabel) {
    if (!this.isQuantityOnlyMode() || !sizeLabel) return;
    if (!this.hasColorSelected()) {
      this.showSizeColorRequiredError();
      return;
    }

    this.selectedQtyOnlySize = sizeLabel;
    this.quantities.clear();
    this.sizesWrap?.classList.remove('is-size-error');
    this.sizeHint?.classList.remove('is-error');
    this.renderQtyOnlySizePicker();
    this.syncStandardQuantitySelector();
    this.updateTotal();
  }

  renderQtyOnlySizePicker() {
    if (!this.isQuantityOnlyMode() || !this.qtyOnlySizeList) return;

    const labels = this.getAllSizeLabels();
    const canSelect = this.hasColorSelected();
    this.qtyOnlySizeList.innerHTML = '';

    if (!labels.length) {
      this.qtyOnlySizeList.innerHTML = `<p class="vf-wholesale__empty">${this.config.i18n.selectOne}</p>`;
      return;
    }

    labels.forEach((label) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vf-wholesale__qty-only-size-btn';
      btn.dataset.vfQtyOnlySize = label;
      btn.textContent = label;
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', label === this.selectedQtyOnlySize ? 'true' : 'false');
      btn.classList.toggle('is-selected', label === this.selectedQtyOnlySize);
      btn.disabled = !canSelect;
      btn.addEventListener('click', () => this.setQtyOnlySizeSelection(label));
      this.qtyOnlySizeList.appendChild(btn);
    });
  }

  syncStandardQuantitySelector() {
    if (!this.isQuantityOnlyMode()) return;

    const canEdit = this.canEditQtyOnlyQuantity();

    if (this.standardQtyInput) {
      this.standardQtyInput.disabled = !canEdit;
    }
    if (this.standardQtyMinus) {
      this.standardQtyMinus.disabled = !canEdit;
    }
    if (this.standardQtyPlus) {
      this.standardQtyPlus.disabled = !canEdit;
    }

    if (canEdit) {
      this.ensureStandardQuantityDefault();
    } else {
      this.quantities.clear();
      if (this.standardQtyInput) this.standardQtyInput.value = '1';
    }

    this.syncSizeHint();
  }

  renderSizeGrid() {
    if (this.isQuantityOnlyMode()) {
      this.renderQtyOnlySizePicker();
      this.syncStandardQuantitySelector();
      return;
    }

    if (!this.sizeGrid) return;
    this.sizeGrid.innerHTML = '';

    const labels = this.getAllSizeLabels();
    if (!labels.length) {
      this.sizeGrid.innerHTML = `<p class="vf-wholesale__empty">${this.config.i18n.selectOne}</p>`;
      return;
    }

    const canEditQty = this.hasColorSelected();

    labels.forEach((label) => {
      const variant = this.getVariantForSize(label);
      const variantId = variant?.id;
      const qtyKey = variantId ? this.quantityKey(variantId, label) : null;
      const qty = qtyKey ? this.quantities.get(qtyKey) || 0 : 0;
      const cell = document.createElement('div');
      cell.className = 'vf-wholesale__size-cell';
      if (!canEditQty) cell.classList.add('is-disabled');
      cell.innerHTML = `
        <span class="vf-wholesale__size-label">${label}</span>
        <div class="vf-wholesale__qty" data-vf-qty-wrap${variantId ? ` data-variant-id="${variantId}"` : ''} data-size-label="${label}">
          <button type="button" class="vf-wholesale__qty-btn" data-vf-qty-minus aria-label="-" ${canEditQty ? '' : 'disabled'}>−</button>
          <input
            type="number"
            class="vf-wholesale__qty-input"
            data-vf-qty-input
            min="0"
            max="${MAX_QTY}"
            step="1"
            inputmode="numeric"
            value="${String(qty).padStart(2, '0')}"
            aria-label="${label}"
            ${canEditQty ? '' : 'disabled readonly'}
          >
          <button type="button" class="vf-wholesale__qty-btn" data-vf-qty-plus aria-label="+" ${canEditQty ? '' : 'disabled'}>+</button>
        </div>
      `;

      if (!canEditQty || !variantId || !qtyKey) {
        this.sizeGrid.appendChild(cell);
        return;
      }

      const input = cell.querySelector('[data-vf-qty-input]');
      const minus = cell.querySelector('[data-vf-qty-minus]');
      const plus = cell.querySelector('[data-vf-qty-plus]');

      if (qty > 0) cell.classList.add('has-qty');

      minus.addEventListener('click', () =>
        this.setQuantityForSizeLabel(label, (this.quantities.get(qtyKey) || 0) - 1, { sourceCell: cell })
      );
      plus.addEventListener('click', () =>
        this.setQuantityForSizeLabel(label, (this.quantities.get(qtyKey) || 0) + 1, { sourceCell: cell })
      );
      input.addEventListener('change', () =>
        this.setQuantityForSizeLabel(label, Number(input.value) || 0, { sourceCell: cell })
      );
      input.addEventListener('blur', () => {
        input.value = String(this.quantities.get(qtyKey) || 0).padStart(2, '0');
      });

      minus.disabled = qty <= 0;
      this.sizeGrid.appendChild(cell);
    });

    this.syncSizeHint();
  }

  getTotalQuantity() {
    let total = 0;
    this.quantities.forEach((qty) => {
      total += qty;
    });
    return total;
  }

  getUnitPrice(totalQty) {
    const { tier1UnitPrice, tier2Min, tier2UnitPrice } = this.config;
    if (totalQty >= tier2Min && tier2UnitPrice > 0) return tier2UnitPrice;
    return tier1UnitPrice;
  }

  getActiveTier(totalQty) {
    const { tier2Min, tier2UnitPrice } = this.config;
    if (totalQty >= tier2Min && tier2UnitPrice > 0) return 2;
    return 1;
  }

  getQtyOnlyPricingRoot() {
    return this.pdpRoot.querySelector('[data-vf-qty-only-pricing]');
  }

  updateQtyOnlyTierNote(totalQty) {
    const root = this.getQtyOnlyPricingRoot();
    const note = root?.querySelector('[data-vf-qty-only-tier-note]');
    if (!note) return;

    const { tier2Min, tier2UnitPrice, tier1UnitPrice } = this.config;
    if (tier2UnitPrice <= 0 || totalQty <= 0) {
      note.hidden = true;
      note.textContent = '';
      return;
    }

    if (totalQty >= tier2Min) {
      note.hidden = false;
      note.textContent = applyI18nTemplate(this.config.i18n.tierApplied, {
        price: formatMoney(tier2UnitPrice, this.config.moneyFormat),
      });
      return;
    }

    const remaining = tier2Min - totalQty;
    note.hidden = false;
    note.textContent = applyI18nTemplate(this.config.i18n.tierRemaining, {
      count: remaining,
      price: formatMoney(tier2UnitPrice, this.config.moneyFormat),
      savings: formatMoney(Math.max(tier1UnitPrice - tier2UnitPrice, 0), this.config.moneyFormat),
    });
  }

  updateTierHighlight(totalQty) {
    const activeTier = this.getActiveTier(totalQty);

    if (this.isQuantityOnlyMode()) {
      const root = this.getQtyOnlyPricingRoot();
      root?.querySelector('[data-vf-qty-only-tier-row="1"]')?.classList.toggle('is-active', activeTier === 1);
      root?.querySelector('[data-vf-qty-only-tier-row="2"]')?.classList.toggle('is-active', activeTier === 2);
      this.updateQtyOnlyTierNote(totalQty);
      return;
    }

    this.tier1Row?.classList.toggle('is-active', activeTier === 1);
    this.tier2Row?.classList.toggle('is-active', activeTier === 2);

    if (!this.tierHint) return;

    const { tier2Min, tier2UnitPrice, tier1UnitPrice } = this.config;
    if (tier2UnitPrice <= 0 || totalQty <= 0) {
      this.tierHint.hidden = true;
      this.tierHint.textContent = '';
      return;
    }

    if (totalQty >= tier2Min) {
      this.tierHint.hidden = false;
      this.tierHint.textContent = applyI18nTemplate(this.config.i18n.tierApplied, {
        price: formatMoney(tier2UnitPrice, this.config.moneyFormat),
      });
      return;
    }

    const remaining = tier2Min - totalQty;
    this.tierHint.hidden = false;
    this.tierHint.textContent = applyI18nTemplate(this.config.i18n.tierRemaining, {
      count: remaining,
      price: formatMoney(tier2UnitPrice, this.config.moneyFormat),
      savings: formatMoney(Math.max(tier1UnitPrice - tier2UnitPrice, 0), this.config.moneyFormat),
    });
  }

  syncHeroPrice(unitPrice, totalQty) {
    if (this.isQuantityOnlyMode()) {
      const pdp = this.pdpRoot || this.closest('[data-md-pdp]');
      const unitEls = pdp?.querySelectorAll('[data-vf-qty-only-unit-price]') || [];
      const instEls = pdp?.querySelectorAll('[data-vf-qty-only-installments]') || [];

      unitEls.forEach((unitEl) => {
        unitEl.textContent = formatMoney(unitPrice, this.config.moneyFormat);
      });

      if (this.config.installmentCount > 0) {
        const installmentValue = Math.round(unitPrice / this.config.installmentCount);
        const installmentText = applyI18nTemplate(this.config.i18n.installmentsTemplate, {
          count: this.config.installmentCount,
          price: formatMoney(installmentValue, this.config.moneyFormat),
        });
        instEls.forEach((instEl) => {
          instEl.textContent = installmentText;
        });
      }
    } else {
      if (this.heroUnitPrice) {
        this.heroUnitPrice.textContent = formatMoney(unitPrice, this.config.moneyFormat);
      }

      if (this.heroInstallments && this.config.installmentCount > 0) {
        const installmentValue = Math.round(unitPrice / this.config.installmentCount);
        this.heroInstallments.textContent = applyI18nTemplate(this.config.i18n.installmentsTemplate, {
          count: this.config.installmentCount,
          price: formatMoney(installmentValue, this.config.moneyFormat),
        });
      }
    }

    const pdp = this.closest('[data-md-pdp]');
    if (!pdp) return;

    const pixEl = pdp.querySelector('[data-md-pix]');
    if (pixEl) {
      pixEl.textContent = formatMoney(unitPrice, this.config.moneyFormat);
    }

    const instEl = pdp.querySelector('[data-md-installments]');
    if (instEl && this.config.installmentCount > 0) {
      const tpl = instEl.dataset.template || this.config.i18n.installmentsTemplate;
      const installmentValue = Math.round(unitPrice / this.config.installmentCount);
      instEl.textContent = applyI18nTemplate(tpl, {
        count: this.config.installmentCount,
        price: formatMoney(installmentValue, this.config.moneyFormat),
      });
    }
  }

  updateTotal() {
    const totalQty = this.getTotalQuantity();
    const unitPrice = this.getUnitPrice(totalQty);
    const totalCents = totalQty * unitPrice;

    if (this.totalEl && !this.isQuantityOnlyMode()) {
      if (totalQty < 1) {
        this.totalEl.textContent = applyI18nTemplate(this.config.i18n.totalTemplate, {
          total: formatMoney(0, this.config.moneyFormat),
        });
      } else {
        this.totalEl.textContent = applyI18nTemplate(this.config.i18n.totalWithQtyTemplate, {
          total: formatMoney(totalCents, this.config.moneyFormat),
          qty: totalQty,
          unit: formatMoney(unitPrice, this.config.moneyFormat),
        });
      }
    }

    this.updateTierHighlight(totalQty);
    this.syncHeroPrice(unitPrice, totalQty);

    if (this.submitBtn) {
      const needsStamp = this.config.showStampLocation !== false;
      const hasStamp = !needsStamp || this.stampLocation !== '';
      const hasColor = this.hasColorSelected();
      const hasSize = this.hasSizeSelected();
      const canSubmit = totalQty >= 1 && hasStamp && hasColor && hasSize && !this._busy;

      this.submitBtn.disabled = !canSubmit;

      if (!hasColor) {
        this.submitBtn.textContent = this.config.i18n.selectColor;
      } else if (!hasSize) {
        this.submitBtn.textContent = this.config.i18n.selectSize || this.config.i18n.selectOne;
      } else if (totalQty < 1) {
        this.submitBtn.textContent = this.config.i18n.selectOne;
      } else if (needsStamp && !hasStamp) {
        this.submitBtn.textContent = this.config.i18n.selectStampLocation;
      } else {
        this.submitBtn.textContent = this.config.i18n.addToCart;
      }
    }

    this.renderStickyBar();
  }

  setFile(file) {
    const { maxFileBytes, i18n } = this.config;

    if (!file) {
      this.file = null;
      if (this.fileInput) this.fileInput.value = '';
      if (this.uploadPreview) this.uploadPreview.hidden = true;
      if (this.dropzone) this.dropzone.hidden = false;
      this.classList.remove('is-filled');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      window.alert(i18n.fileInvalid);
      return;
    }
    if (file.size > maxFileBytes) {
      window.alert(i18n.fileTooLarge);
      return;
    }

    this.file = file;
    const uploadDetails = this.querySelector('.vf-wholesale__upload--compact');
    if (uploadDetails instanceof HTMLDetailsElement) {
      uploadDetails.open = true;
    }
    if (this.uploadPreview && this.uploadThumb && this.uploadName) {
      this.uploadThumb.src = URL.createObjectURL(file);
      this.uploadName.textContent = file.name;
      this.uploadPreview.hidden = false;
    }
    if (this.dropzone) this.dropzone.hidden = true;
    this.classList.add('is-filled');
  }

  createGroupId() {
    const stamp = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `ATC-${stamp}-${rand}`;
  }

  buildLineProperties(variant, quantity, { groupId, totalQty, unitPrice, sizeLabel }) {
    const { i18n, options, sizePosition, colorPosition, colorPropertyName, colorSource } =
      this.config;
    const properties = {};
    const unitMoney = formatMoney(unitPrice, this.config.moneyFormat);
    const sizePropertyName = this.config.sizeOptionName || 'Tamanho';

    properties[i18n.propGroup] = groupId;
    properties[i18n.propOrderTotal] = String(totalQty);
    properties[i18n.propUnitPrice] = unitMoney;
    properties['_atacado_unit_cents'] = String(unitPrice);
    properties['_atacado_tier1_cents'] = String(this.config.tier1UnitPrice);
    properties['_atacado_tier2_cents'] = String(this.config.tier2UnitPrice || 0);
    properties['_atacado_tier2_min'] = String(this.config.tier2Min);

    const gradeParts = [];
    let sizeHandled = false;
    let colorHandled = false;

    if (colorPosition && (colorSource === 'images' || colorSource === 'category')) {
      const colorVal = this.selection[colorPosition];
      if (colorVal) {
        const colorName = colorPropertyName || 'Cor';
        properties[colorName] = colorVal;
        gradeParts.push(colorVal);
        colorHandled = true;
      }
    }

    (options || []).forEach((optionName, index) => {
      const value = this.getOptionValue(variant, index + 1);
      if (!value) return;
      if (optionName === 'Title' && value === 'Default Title') return;
      if (colorHandled && index + 1 === colorPosition) return;

      properties[optionName] = value;
      gradeParts.push(index + 1 === sizePosition ? `${value} × ${quantity}` : value);
      if (index + 1 === sizePosition) sizeHandled = true;
    });

    if (sizeLabel && !sizeHandled) {
      properties[sizePropertyName] = sizeLabel;
      gradeParts.push(`${sizeLabel} × ${quantity}`);
    }

    if (gradeParts.length) {
      properties[i18n.propGrade] = gradeParts.join(' / ');
    }

    if (this.config.showStampLocation !== false && this.stampLocation) {
      properties[this.config.stampLocationProperty] = this.stampLocation;
    }

    properties['_grupo_atacado'] = groupId;
    properties['_preco_atacado'] = applyI18nTemplate(i18n.cartTierNote, {
      price: unitMoney,
      qty: totalQty,
    });

    return properties;
  }

  buildCartItems() {
    const groupId = this.createGroupId();
    const totalQty = this.getTotalQuantity();
    const unitPrice = this.getUnitPrice(totalQty);
    const sizeValues = this.getSizeValues();
    const sizeOrder = sizeValues.map(({ label, variant }) =>
      this.isCategorySizeMode() ? label : variant.id
    );
    const entries = [];

    this.quantities.forEach((quantity, key) => {
      if (quantity < 1) return;

      if (this.isCategorySizeMode() && String(key).startsWith('size:')) {
        const sizeLabel = String(key).slice(5);
        const variant = this.getDefaultVariant();
        if (!variant) return;
        entries.push({ variantId: variant.id, quantity, sizeLabel });
        return;
      }

      entries.push({ variantId: Number(key), quantity, sizeLabel: null });
    });

    entries.sort((a, b) => {
      const aIndex = a.sizeLabel
        ? sizeOrder.indexOf(a.sizeLabel)
        : sizeOrder.indexOf(a.variantId);
      const bIndex = b.sizeLabel
        ? sizeOrder.indexOf(b.sizeLabel)
        : sizeOrder.indexOf(b.variantId);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

    return entries
      .map(({ variantId, quantity, sizeLabel }) => {
        const variant = this.config.variants.find((v) => v.id === variantId);
        if (!variant) return null;
        return {
          id: variantId,
          quantity,
          properties: this.buildLineProperties(variant, quantity, {
            groupId,
            totalQty,
            unitPrice,
            sizeLabel,
          }),
        };
      })
      .filter(Boolean);
  }

  getSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  async addItemsWithOptionalFile(items) {
    if (!items.length) return null;

    const sections = this.getSectionIds().join(',');

    if (this.file) {
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const fd = new FormData();
        fd.append('id', String(item.id));
        fd.append('quantity', String(item.quantity));

        Object.entries(item.properties || {}).forEach(([key, value]) => {
          fd.append(`properties[${key}]`, value);
        });

        if (index === 0) {
          fd.append(`properties[${this.config.uploadProperty}]`, this.file);
        } else {
          fd.append(`properties[${this.config.uploadProperty}]`, this.file.name);
        }

        if (sections) fd.append('sections', sections);

        const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: fd,
        });
        const json = await res.json();
        if (json.status) throw json;
        if (index === items.length - 1) return json;
      }
    }

    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections }),
    });
    const json = await res.json();
    if (json.status) throw json;
    return json;
  }

  async handleSubmit() {
    if (this._busy) return;

    const items = this.buildCartItems();
    if (!items.length) {
      this.promptMissingSelection();
      return;
    }

    this._busy = true;
    this.submitBtn?.classList.add('is-busy');
    if (this.submitBtn) this.submitBtn.textContent = this.config.i18n.adding;
    this.updateTotal();

    try {
      const response = await this.addItemsWithOptionalFile(items);
      const totalQty = this.getTotalQuantity();

      notifyCartAddAndOpen(response, this.dataset.sectionId || '', {
        source: 'vestfirma-pdp-wholesale',
        itemCount: totalQty,
        productId: String(this.config.productId),
        sections: response?.sections,
      });

      if (this.submitBtn) this.submitBtn.textContent = this.config.i18n.added;
      this.quantities.clear();
      if (this.isQuantityOnlyMode()) {
        if (this.standardQtyInput) this.standardQtyInput.value = '1';
      }
      this.renderSizeGrid();
      this.updateTotal();
    } catch (error) {
      const message = error?.message || error?.description || this.config.i18n.error;
      this.dispatchEvent(
        new CartErrorEvent(this.dataset.sectionId || '', message, error?.description || '', error?.errors)
      );
      window.alert(message);
    } finally {
      this._busy = false;
      this.submitBtn?.classList.remove('is-busy');
      this.updateTotal();
    }
  }
}

if (!customElements.get('vestfirma-pdp-wholesale')) {
  customElements.define('vestfirma-pdp-wholesale', VestfirmaPdpWholesale);
}

export { VestfirmaPdpWholesale };
